import { useContext, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Anchor, Box, Paragraph } from '@zillow/constellation-10';
import { LoadingButton } from '@zg-rentals/loading-components';
import { CreateNewTaskModal, NoTasksEmptyState, TasksTable } from '@zg-rentals/maintenance-utils';

import { useSelector } from '../../selectors/useSelector';

import ConnectedModuleCard from '../ConnectedModuleCard';
import { useGetMaintenanceTasks } from '../../hooks/queries/maintenance';
import { RMWContext } from '../../providers/RMWContext';

const ConnectedMaintenanceModule = () => {
  const { paths } = useContext(RMWContext);
  const { listingAlias } = useSelector((state) => {
    return {
      listingAlias: state.properties.currentProperty.listingAlias,
    };
  }, shallowEqual);

  const [displayCreateNewTaskModal, setDisplayCreateNewTaskModal] = useState(false);

  // use isLoading condition in order apply conditional logic on the initial request
  const { data, isLoading, error } = useGetMaintenanceTasks(listingAlias);

  if (error) {
    return <Paragraph>Error occurred.</Paragraph>;
  }

  const handleRowClick = (taskId: string) => {
    window.router.updateLocation(`/rental-manager/maintenance-tasks/task?id=${taskId}`);
  };
  const hasMaintenanceTasks = data?.maintenanceTasks?.length > 0;
  return (
    <div data-testid="ConnectedMaintenanceModule">
      {hasMaintenanceTasks || isLoading ? (
        <ConnectedModuleCard
          title="Maintenance"
          ctaComponent={
            <LoadingButton
              tone="brand"
              emphasis="filled"
              onClick={() => setDisplayCreateNewTaskModal(true)}
              isLoading={isLoading}
              loadingWidth="10rem"
            >
              Create new task
            </LoadingButton>
          }
        >
          <TasksTable
            listingAlias={listingAlias}
            fromExternalApp={true}
            showAddressColumn={false}
            tasksToDisplay={data?.maintenanceTasks}
            onRowClick={handleRowClick}
            isLoading={isLoading}
          />
          <Box css={{ display: 'flex', justifyContent: 'center' }}>
            <LoadingButton
              asChild={true}
              fluid={false}
              isLoading={isLoading}
              loadingWidth="50%"
              css={{ marginTop: '300' }}
            >
              <Anchor href={paths.maintenanceTrackerPage}>View tasks for all properties</Anchor>
            </LoadingButton>
          </Box>
        </ConnectedModuleCard>
      ) : (
        <NoTasksEmptyState setDisplayCreateNewTaskModal={setDisplayCreateNewTaskModal} />
      )}

      {displayCreateNewTaskModal && (
        <CreateNewTaskModal
          listingAlias={listingAlias}
          onClose={(newTaskId?: string) => {
            if (newTaskId) {
              window.location.href = `${paths.maintenanceTrackerPage}/task?id=${newTaskId}`;
            }
            setDisplayCreateNewTaskModal(false);
          }}
        />
      )}
    </div>
  );
};

export default ConnectedMaintenanceModule;
